import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useDeleteKnowledgeItem } from "@/hooks/knowledge/useKnowledgeItems";
import { KnowledgeItem } from "@/hooks/knowledge/useKnowledgeItems";
import { ExternalLink, Calendar, FileText, Trash2, Info, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type CrawlStage = 'INITIATING' | 'CRAWLING' | 'PROCESSING' | 'FINALIZING' | 'COMPLETED' | null;

interface EditLinkFormProps {
    item: KnowledgeItem;
    onSuccess: (item?: KnowledgeItem) => void;
}

const EditLinkForm = ({ item, onSuccess }: EditLinkFormProps) => {
    const { toast } = useToast();
    const { deleteItem, isLoading: isDeleting } = useDeleteKnowledgeItem();
    const { organization } = useAuth();
    const [isRecrawling, setIsRecrawling] = useState(false);
    const [crawlStage, setCrawlStage] = useState<CrawlStage>(null);

    // Format the last crawled date
    const formatDate = (dateString: string | null) => {
        if (!dateString) return "Never";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long", 
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    // Truncate content for preview
    const getContentPreview = (content: string | null) => {
        if (!content) return "No content available";
        const maxLength = 300;
        if (content.length <= maxLength) return content;
        return content.substring(0, maxLength) + "...";
    };

    const handleDelete = async () => {
        try {
            await deleteItem(item.id, "link");
            toast({
                title: "Link deleted",
                description: "The link has been successfully removed from your knowledge base.",
            });
            onSuccess();
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to delete the link. Please try again.",
                variant: "destructive",
            });
        }
    };

    const handleRecrawl = async () => {
        if (!organization?.id || !item.url) {
            toast({
                title: "Error",
                description: "Missing required information for recrawling.",
                variant: "destructive",
            });
            return;
        }

        setIsRecrawling(true);
        setCrawlStage('INITIATING');

        try {
            setCrawlStage('CRAWLING');

            // Get the user session for authentication
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
                throw new Error("Authentication required");
            }

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/firecrawl-crawl`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    url: item.url,
                    linkId: item.id,
                    title: item.title,
                    description: item.description,
                    organizationId: organization.id,
                    agentId: item.agent_id,
                    limit: 1,
                    scrapeOptions: {
                        timeout: 30000,
                        formats: ["markdown"],
                        removeBase64Images: true,
                        onlyMainContent: true,
                        blockAds: true,
                        waitFor: 2000,
                        mobile: false
                    }
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to initiate recrawl: ${response.status} ${errorText}`);
            }

            const result = await response.json();
            
            if (result.id) {
                // Recrawl initiated successfully - update database with crawl_id
                const { error: updateError } = await supabase
                    .from("knowledge_items")
                    .update({
                        crawl_id: result.id,
                        crawl_status: "crawling",
                        updated_at: new Date().toISOString()
                    })
                    .eq("id", item.id);

                if (updateError) {
                    console.error("Failed to update crawl status:", updateError);
                    throw new Error("Failed to update crawl status");
                }

                console.log(`Recrawl initiated successfully with ID: ${result.id}`);
                setCrawlStage('COMPLETED');

                toast({
                    title: "Recrawl initiated",
                    description: "The website is being recrawled. Content will be updated when complete.",
                });
                
                // Refresh the parent component after showing completion
                setTimeout(() => {
                    onSuccess(item);
                }, 1500);
            } else {
                throw new Error("No crawl ID returned from Firecrawl");
            }
        } catch (error) {
            console.error("Recrawl error:", error);
            toast({
                title: "Recrawl failed",
                description: error instanceof Error ? error.message : "Failed to recrawl the website. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsRecrawling(false);
            setCrawlStage(null);
        }
    };

    const getStageText = (stage: CrawlStage) => {
        switch (stage) {
            case 'INITIATING': return 'Preparing to crawl...';
            case 'CRAWLING': return 'Crawling website...';
            case 'PROCESSING': return 'Processing content...';
            case 'FINALIZING': return 'Finalizing...';
            case 'COMPLETED': return 'Completed!';
            default: return '';
        }
    };

    return (
        <div className="space-y-8 max-h-[70vh] overflow-y-auto px-1">
            {/* Information Card */}
            <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                    Links cannot be edited once crawled. If you need to update the content, delete this link and create a new one with your desired website.
                </AlertDescription>
            </Alert>

            {/* Link Information */}
            <div className="space-y-6">
                {/* Title */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">Title</Label>
                    <div className="text-sm font-medium">{item.title}</div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">Description</Label>
                    <div className="text-sm text-muted-foreground">
                        {item.description || "No description provided"}
                    </div>
                </div>

                {/* URL */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">URL</Label>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground flex-1 truncate font-mono">
                            {item.url}
                        </span>
                        <Button variant="ghost" size="sm" className="shrink-0" asChild>
                            <a href={item.url || "#"} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                            </a>
                        </Button>
                    </div>
                </div>

                {/* Last Crawled */}
                <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Calendar className="h-4 w-4" />
                        Last Crawled
                    </Label>
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">
                            {formatDate(item.updated_at)}
                        </div>
                        <Button 
                            onClick={handleRecrawl} 
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            disabled={isRecrawling || isDeleting}
                        >
                            <RefreshCw className={`h-4 w-4 ${isRecrawling ? 'animate-spin' : ''}`} />
                            {isRecrawling ? "Recrawling..." : "Recrawl"}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content Preview */}
            <div className="space-y-3">
                <Label className="text-sm font-medium text-foreground">Content Preview</Label>
                <Card className="bg-gray-50 border-0">
                    <CardContent className="p-5">
                        <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                            {getContentPreview(item.content)}
                        </div>
                        {item.content && item.content.length > 300 && (
                            <p className="text-xs text-muted-foreground mt-4">
                                Showing first 300 characters of {item.content.length.toLocaleString()} total characters.
                            </p>
                        )}
                        {!item.content && (
                            <p className="text-xs text-muted-foreground italic">
                                This link has not been crawled yet or the crawl failed.
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Recrawl Progress */}
            {isRecrawling && (
                <Alert className="bg-blue-50 border-blue-200">
                    <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />
                    <AlertDescription className="text-blue-800">
                        {getStageText(crawlStage)}
                    </AlertDescription>
                </Alert>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end pt-6">
                <Button 
                    onClick={handleDelete} 
                    className="min-w-[120px] gap-2 bg-red-600 hover:bg-red-700 text-white"
                    disabled={isDeleting || isRecrawling}
                >
                    <Trash2 className="h-4 w-4" />
                    {isDeleting ? "Deleting..." : "Delete"}
                </Button>
            </div>
        </div>
    );
};

export default EditLinkForm; 